"use client";

import { useTranslations } from "next-intl";
import styled from "styled-components";

const LegalContainer = styled.div<{ $onPage: boolean }>`
  height: fit-content;
  overflow: auto;
  max-height: 75vh;
  margin-bottom: 50px;
  padding: ${(props) => (props.$onPage ? "30px" : "10px")};

  h1 {
    margin: 0 0 20px 0;
  }

  h3 {
    margin: 20px 0 10px 0;
  }

  p span {
    color: ${(props) => props.theme.black};
    font-weight: bold;
    text-decoration: none !important;
  }

  li span {
    text-decoration: underline;
  }

  .center-align {
    text-align: center;
    width: 100%;
  }

  .upper {
    text-transform: uppercase;
    margin-bottom: 5px;
  }

  ol,
  ul {
    color: ${(props) => props.theme.black};
    margin: 10px 0 0 35px;
    font-size: 0.95rem;

    li {
      margin-bottom: 5px;
    }
  }

  p {
    color: ${(props) => props.theme.black};
    font-size: 0.95rem;
  }

  a {
    color: ${(props) => props.theme.link};

    &:hover {
      color: ${(props) => props.theme.black};
    }
  }

  @media (max-width: 425px) {
    max-height: 92vh;
    padding: ${(props) => (props.$onPage ? "20px" : "0")};
    padding: 0px 15px;
    font-size: 0.75rem !important;

    p,
    ol,
    ul {
      font-size: 0.75rem !important;
    }
  }
`;

export function USPatientPrivacyPolicyContent({
  onPage,
}: {
  onPage?: boolean;
}) {
  const t = useTranslations("termsOfAcceptance.usPatientPrivacyPolicy");

  return (
    <LegalContainer $onPage={onPage || false}>
      <h1>{t("title")}</h1>
      <br />
      <p>
        {t.rich("intro1", {
          link: (chunks) => (
            <span style={{ textDecoration: "none", fontWeight: "bold" }}>
              {chunks}
            </span>
          ),
        })}
      </p>
      <br />
      <p>{t("intro2")}</p>
      <br />
      <h3>{t("useByMinors")}</h3>
      <br />
      <p>{t("useByMinorsText")}</p>
      <br />
      <h3>{t("informationCollection")}</h3>
      <br />
      <p>{t("informationCollectionPara1")}</p>
      <br />
      <p>{t("informationCollectionPara2")}</p>
      <ul>
        <li>
          {t.rich("infoUL1", {
            link: (chunks) => (
              <span style={{ textDecoration: "none", fontWeight: "bold" }}>
                {chunks}
              </span>
            ),
          })}
        </li>
        <li>
          {t.rich("infoUL2", {
            link: (chunks) => (
              <span style={{ textDecoration: "none", fontWeight: "bold" }}>
                {chunks}
              </span>
            ),
          })}
        </li>
        <li>
          {t.rich("infoUL3", {
            link: (chunks) => (
              <span style={{ textDecoration: "none", fontWeight: "bold" }}>
                {chunks}
              </span>
            ),
          })}
        </li>
        <li>
          {t.rich("infoUL4", {
            link: (chunks) => (
              <span style={{ textDecoration: "none", fontWeight: "bold" }}>
                {chunks}
              </span>
            ),
          })}
          <ul>
            <li>{t("infoSubUL1")}</li>
            <li>{t("infoSubUL2")}</li>
            <li>{t("infoSubUL3")}</li>
          </ul>
        </li>
        <li>{t("infoUL5")}</li>
        <li>{t("infoUL6")}</li>
        <li>{t("infoUL7")}</li>
      </ul>
      <p>{t("informationCollectionPara3")}</p>
      <br />
      <p>{t("informationCollectionPara4")}</p>
      <br />
      <p>{t("informationCollectionPara5")}</p>
      <br />
      <p>{t("informationCollectionPara6")}</p>
      <br />
      <p>{t("informationCollectionPara7")}</p>
      <br />
      <h3>{t("howWeUse")}</h3>
      <br />
      <p>{t("howWeUsePara1")}</p>
      <br />
      <p>{t("howWeUsePara2")}</p>
      <ul>
        <li>{t("howWeUseUL1")}</li>
        <li>{t("howWeUseUL2")}</li>
        <li>{t("howWeUseUL3")}</li>
        <li>{t("howWeUseUL4")}</li>
        <li>{t("howWeUseUL5")}</li>
        <li>{t("howWeUseUL6")}</li>
        <li>{t("howWeUseUL7")}</li>
        <li>{t("howWeUseUL8")}</li>
        <li>{t("howWeUseUL9")}</li>
        <li>{t("howWeUseUL10")}</li>
        <li>{t("howWeUseUL11")}</li>
      </ul>
      <p>{t("howWeUsePara3")}</p>
      <ul>
        <li>
          {t.rich("disclosureUL1", {
            link: (chunks) => {
              const text = chunks!.toString();
              if (text.includes("http")) {
                return (
                  <span style={{ textDecoration: "none" }}>
                    <a href={text} target="_blank" rel="noopener noreferrer">
                      {text}
                    </a>
                  </span>
                );
              }
              return (
                <span style={{ textDecoration: "none", fontWeight: "bold" }}>
                  {chunks}
                </span>
              );
            },
          })}
        </li>
        <li>{t("disclosureUL2")}</li>
        <li>{t("disclosureUL3")}</li>
        <li>{t("disclosureUL4")}</li>
      </ul>
      <p>{t("howWeUsePara4")}</p>
      <br />
      <h3>{t("locallyStored")}</h3>
      <br />
      <p>{t("locallyStoredText")}</p>
      <br />
      <h3>{t("choicesAccess")}</h3>
      <br />
      <p style={{ fontWeight: "bold" }}>{t("choicesSubtitle")}</p>
      <p>{t("choicesText")}</p>
      <br />
      <p style={{ fontWeight: "bold" }}>{t("accessSubtitle")}</p>
      <p>{t("accessText")}</p>
      <br />
      <h3>{t("security")}</h3>
      <br />
      <p>{t("securityText")}</p>
      <br />
      <h3>{t("retentionPeriod")}</h3>
      <br />
      <p>{t("retentionPeriodText")}</p>
      <br />
      <h3>{t("thirdPartySites")}</h3>
      <br />
      <p>{t("thirdPartySitesText")}</p>
      <br />
      <h3>{t("contactingUs")}</h3>
      <br />
      <p>{t("contactingUsPara1")}</p>
      <br />
      <p>{t("contactingUsPara2")}</p>
      <p style={{ fontWeight: "bold", marginTop: "10px" }}>
        {t("contactCompany")}
      </p>
      <p style={{ fontWeight: "bold" }}>{t("contactCity")}</p>
      <p style={{ fontWeight: "bold" }}>
        {t.rich("contactPhone", {
          link: (chunks) => {
            const text = chunks!.toString();
            if (/^[\d\s\-()]+$/.test(text.trim())) {
              return <a href={`tel:${text.replace(/[\s()-]/g, "")}`}>{text}</a>;
            }
            return chunks;
          },
        })}
      </p>
      <p style={{ fontWeight: "bold" }}>{t("contactHours")}</p>
      <p style={{ fontWeight: "bold" }}>
        {t.rich("contactEmail", {
          link: (chunks) => {
            const text = chunks!.toString();
            if (text.includes("@")) {
              return <a href={`mailto:${text}`}>{text}</a>;
            }
            return chunks;
          },
        })}
      </p>
      <br />
      <h3>{t("updates")}</h3>
      <br />
      <p>{t("updatesText")}</p>
    </LegalContainer>
  );
}
